import {
  IBulkOfferingItem,
  IBulkQuoteOfferingReqSchema,
  OfferingTypesEnum,
} from '@monetize/types/app';
import isNil from 'lodash/isNil';
import { useEffect, useMemo, useState } from 'react';
import {
  Control,
  useFieldArray,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { MGrid, MText } from '../../../../../components/Monetize';
import BulkRatesRow from './BulkRatesRow';
import { getBillingFrequencyPhrase } from './utils/bulkOfferingUtils';

export const BulkRates = ({
  setValue,
  control,
  watch,
  disabled,
}: {
  setValue: UseFormSetValue<IBulkQuoteOfferingReqSchema>;
  control: Control<IBulkQuoteOfferingReqSchema>;
  watch: UseFormWatch<IBulkQuoteOfferingReqSchema>;
  disabled?: boolean;
}) => {
  const [errorMessages, setErrorMessage] = useState<
    Record<string, string> | undefined
  >();

  const offerings = watch('offerings');
  const bulkRate = watch('bulkRate');
  const billingFrequency = watch('billingFrequency');

  const itemListByOfferingId = useMemo(() => {
    type BulkRate = {
      id: string;
      name: string;
      billingFrequencyInMonths?: number | null;
    };

    return offerings.reduce(
      (
        acc: Record<
          string,
          {
            items: BulkRate[];
            isBillingFrequencyMatch: boolean;
            isOneTime: boolean;
          }
        >,
        offering: IBulkOfferingItem,
      ) => {
        // One-time offerings are not billed on a frequency; any rate can be picked
        if (offering.type === OfferingTypesEnum.ONETIME) {
          acc[offering.id] = {
            items: offering.rates ?? [],
            isBillingFrequencyMatch: false,
            isOneTime: true,
          };
          return acc;
        }

        const filteredRates =
          offering.rates?.filter(
            (rate) =>
              !isNil(rate.billingFrequencyInMonths) &&
              rate.billingFrequencyInMonths === Number(billingFrequency),
          ) ?? [];

        // If no rates match, return the original rates, otherwise return the filtered rates
        acc[offering.id] = {
          items:
            filteredRates.length > 0 ? filteredRates : (offering.rates ?? []),
          isBillingFrequencyMatch: filteredRates.length > 0,
          isOneTime: false,
        };

        return acc;
      },
      {},
    );
  }, [offerings, billingFrequency]);

  const { fields, remove } = useFieldArray<
    IBulkQuoteOfferingReqSchema,
    'bulkRate'
  >({
    control,
    name: 'bulkRate',
  });

  // Auto-select rate when there is only one available and validate errors
  useEffect(() => {
    if (fields && itemListByOfferingId) {
      const newErrorMessages: Record<string, string> = {};

      fields.forEach((field, index) => {
        const ratesForOffering = itemListByOfferingId[field.offeringId]?.items;
        const isBillingFrequencyMatch =
          itemListByOfferingId[field.offeringId]?.isBillingFrequencyMatch;

        // Keeps the first rate `resolveBulkRates` gave it, or the user's pick
        if (itemListByOfferingId[field.offeringId]?.isOneTime) {
          return;
        }

        // If no rates available, show error only when billing frequency is selected
        if (!ratesForOffering || ratesForOffering.length === 0) {
          if (billingFrequency) {
            newErrorMessages[field.offeringId] =
              `No rate is billed ${getBillingFrequencyPhrase(billingFrequency)}. Choose another rate.`;
          }
          return;
        }

        // Case 1: Only 1 rate available - always select it regardless of billing frequency
        if (ratesForOffering.length === 1) {
          setValue(`bulkRate.${index}.rateId`, ratesForOffering[0].id);
          return;
        }

        // Case 2: Multiple rates available, but no billing frequency selected
        // Auto-select annual rate (12 months) by default, or first rate if no annual
        if (!billingFrequency) {
          const annualRate = ratesForOffering.find(
            (rate) => rate.billingFrequencyInMonths === 12,
          );
          setValue(
            `bulkRate.${index}.rateId`,
            annualRate ? annualRate.id : ratesForOffering[0].id,
          );
          return;
        }

        // Case 3: Multiple rates available and billing frequency is selected
        if (isBillingFrequencyMatch) {
          // Rates match the billing frequency - auto-select the first matching rate
          setValue(`bulkRate.${index}.rateId`, ratesForOffering[0].id);
        } else {
          // No rates match the billing frequency - show error
          newErrorMessages[field.offeringId] =
            `No rate is billed ${getBillingFrequencyPhrase(billingFrequency)}. Choose another rate.`;
        }
      });

      setErrorMessage(newErrorMessages);
    }
  }, [fields, itemListByOfferingId, billingFrequency, setValue]);

  const handleErrorMessageValidation = (offeringId: string) => {
    if (errorMessages?.[offeringId]) {
      const updatedErrorMessages = { ...errorMessages };
      delete updatedErrorMessages[offeringId];
      setErrorMessage(updatedErrorMessages);
    }
  };

  const handleRemoveOffering = (offeringId: string) => {
    // Remove from offerings
    setValue(
      'offerings',
      offerings.filter((offering) => offering.id !== offeringId),
    );

    // Find the index of the offering to remove from bulkRate
    const indexToRemove = fields.findIndex(
      (field) => field.offeringId === offeringId,
    );

    if (indexToRemove !== -1) {
      remove(indexToRemove);
    }

    // Also remove any error messages for this offering
    if (errorMessages?.[offeringId]) {
      const updatedErrorMessages = { ...errorMessages };
      delete updatedErrorMessages[offeringId];
      setErrorMessage(updatedErrorMessages);
    }
  };

  // The name column fits the longest name, so the rate sits next to it
  return (
    <MGrid
      templateColumns="fit-content(28rem) minmax(16rem, 24rem)"
      columnGap={8}
      rowGap={4}
    >
      <MText fontWeight="semibold">Offerings</MText>
      <MText fontWeight="semibold">Rate</MText>
      {fields.map((field, index) => {
        const moreThanOneRate = Boolean(
          itemListByOfferingId[field.offeringId]?.items?.length > 1,
        );
        return (
          <BulkRatesRow
            fieldsCount={fields.length}
            key={`bulkRate.${field.offeringId}`}
            control={control}
            field={field}
            offerings={offerings}
            productIds={bulkRate[index]?.productIds}
            onProductIdsChange={(productIds) =>
              setValue(`bulkRate.${index}.productIds`, productIds)
            }
            errorMessages={errorMessages}
            disabled={disabled}
            index={index}
            itemListByOfferingId={itemListByOfferingId}
            billingFrequency={billingFrequency}
            moreThanOneRate={moreThanOneRate}
            handleErrorMessageValidation={handleErrorMessageValidation}
            removeOffering={handleRemoveOffering}
          />
        );
      })}
    </MGrid>
  );
};
